import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faFile, faFileImage, faFilePdf, faFileWord, faFileExcel, faFileZipper } from '@fortawesome/free-solid-svg-icons';
import type { TaxonomyTerm } from '../../hooks/useTaxonomyTerms';
import { filterCategoriesForFile } from './filterCategoriesForFile';
import type { StagedFile, FileMetadata } from './types';
import type { FieldSchema } from './contentTypeSchemas';
import WildfireAutocomplete from './WildfireAutocomplete';
import './FileMetadataTable.scss';

function mimeIcon(mime: string) {
  if (mime.startsWith('image/')) return faFileImage;
  if (mime === 'application/pdf') return faFilePdf;
  if (mime.includes('word') || mime.includes('.document')) return faFileWord;
  if (mime.includes('excel') || mime.includes('.sheet')) return faFileExcel;
  if (mime.includes('zip')) return faFileZipper;
  return faFile;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isFieldVisible(field: FieldSchema, staged: StagedFile, subcategories: TaxonomyTerm[]): boolean {
  if (!field.visibleWhenSubcategoryName) return true;
  const subId = staged.metadata.subcategoryId;
  if (!subId) return false;
  const term = subcategories.find((t) => t.id === subId);
  return term?.name === field.visibleWhenSubcategoryName;
}

function renderField(
  field: FieldSchema,
  staged: StagedFile,
  hasErrors: boolean,
  isLocked: boolean,
  showCopyToAll: boolean,
  subcategories: TaxonomyTerm[],
  onUpdateMetadata: (id: string, key: keyof FileMetadata, value: string | boolean) => void,
  applyToAll: (field: FieldSchema) => void,
) {
  if (!isFieldVisible(field, staged, subcategories)) return null;

  const raw = staged.metadata[field.key];
  const stringValue = typeof raw === 'string' ? raw : '';
  const boolValue = raw === true;
  const isEmpty = field.type === 'textarea'
    ? !stringValue.trim()
    : field.type === 'boolean'
      ? false
      : !stringValue;
  const showError = hasErrors && field.required && isEmpty;
  const needsDescriptionHint = field.needsDescriptionWhenValueIn?.includes(stringValue) ?? false;

  return (
    <div key={field.key} className="file-meta-card__field">
      {field.type !== 'boolean' && (
        <label>
          {field.label}
          {field.required && <span className="required"> *</span>}
        </label>
      )}
      <div className="file-meta-card__field-row">
        {field.type === 'select' && field.options && (
          <select
            value={stringValue}
            onChange={(e) => onUpdateMetadata(staged.id, field.key, e.target.value)}
            disabled={isLocked}
            className={showError ? 'field--invalid' : ''}
          >
            <option value="">Select</option>
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {field.type === 'select' && !field.options && (
          <select
            value={stringValue}
            onChange={(e) => onUpdateMetadata(staged.id, field.key, e.target.value)}
            disabled={isLocked}
            className={showError ? 'field--invalid' : ''}
          >
            <option value="">Select</option>
            {(field.filterByExtension
              ? filterCategoriesForFile(staged.file, subcategories)
              : subcategories
            ).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}

        {field.type === 'date' && (
          <input
            type="date"
            value={stringValue}
            onChange={(e) => onUpdateMetadata(staged.id, field.key, e.target.value)}
            disabled={isLocked}
            className={showError ? 'field--invalid' : ''}
          />
        )}

        {field.type === 'textarea' && (
          <textarea
            value={stringValue}
            onChange={(e) => onUpdateMetadata(staged.id, field.key, e.target.value)}
            disabled={isLocked}
            rows={2}
            placeholder={field.placeholder}
            className={showError ? 'field--invalid' : ''}
          />
        )}

        {field.type === 'boolean' && (
          <label className="file-meta-card__checkbox">
            <input
              type="checkbox"
              checked={boolValue}
              onChange={(e) => onUpdateMetadata(staged.id, field.key, e.target.checked)}
              disabled={isLocked}
            />
            <span>{field.label}</span>
          </label>
        )}

        {field.type === 'wildfire-autocomplete' && (
          <WildfireAutocomplete
            value={stringValue}
            onChange={(uuid) => onUpdateMetadata(staged.id, field.key, uuid)}
            disabled={isLocked}
            placeholder={field.placeholder}
            currentYearOnly={field.currentYearOnly}
            className={showError ? 'field--invalid' : ''}
          />
        )}

        {showCopyToAll && field.copyToAll && field.type !== 'boolean' && (
          <button
            type="button"
            className="file-meta-card__copy-all"
            onClick={() => applyToAll(field)}
            disabled={isLocked || isEmpty}
          >
            Copy to all
          </button>
        )}
      </div>
      {needsDescriptionHint && (
        <p className="file-meta-card__field-hint">Please specify the source in the Description field.</p>
      )}
    </div>
  );
}

interface FileMetadataTableProps {
  files: StagedFile[];
  fields: FieldSchema[];
  subcategories: TaxonomyTerm[];
  onUpdateMetadata: (id: string, field: keyof FileMetadata, value: string | boolean) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

const FileMetadataTable: React.FC<FileMetadataTableProps> = ({
  files,
  fields,
  subcategories,
  onUpdateMetadata,
  onRemove,
  disabled = false,
}) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, string> = {};
    const toRevoke: string[] = [];

    files.forEach((f) => {
      if (f.file.type.startsWith('image/')) {
        if (previews[f.id]) {
          next[f.id] = previews[f.id];
        } else {
          next[f.id] = URL.createObjectURL(f.file);
        }
      }
    });

    Object.entries(previews).forEach(([id, url]) => {
      if (!next[id]) toRevoke.push(url);
    });
    toRevoke.forEach((url) => URL.revokeObjectURL(url));

    setPreviews(next);

    return () => {
      Object.values(next).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.map((f) => f.id).join(',')]);

  // Auto-clear stale subcategory values that aren't in the file's filtered list
  useEffect(() => {
    files.forEach((f) => {
      if (f.metadata.subcategoryId) {
        const validSubs = filterCategoriesForFile(f.file, subcategories);
        if (!validSubs.find((t) => t.id === f.metadata.subcategoryId)) {
          onUpdateMetadata(f.id, 'subcategoryId', '');
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.map((f) => `${f.id}:${f.metadata.subcategoryId}`).join(',')]);

  const applyToAll = (field: FieldSchema) => {
    const sourceValue = files[0]?.metadata[field.key];
    if (sourceValue === undefined || sourceValue === '' || sourceValue === null) return;
    files.slice(1).forEach((f) => {
      if (field.key === 'subcategoryId' && field.filterByExtension) {
        const validSubs = filterCategoriesForFile(f.file, subcategories);
        if (typeof sourceValue === 'string' && !validSubs.find((t) => t.id === sourceValue)) return;
      }
      onUpdateMetadata(f.id, field.key, sourceValue as string | boolean);
    });
  };

  if (files.length === 0) return null;

  const showCopyToAll = files.length > 1;
  const pendingCount = files.filter((f) => f.status !== 'success').length;
  const successCount = files.filter((f) => f.status === 'success').length;

  return (
    <div className="file-meta-list">
      <div className="file-meta-list__header">
        <span>
          {pendingCount} file{pendingCount !== 1 ? 's' : ''} to upload
          {successCount > 0 && `, ${successCount} already uploaded`}
        </span>
      </div>

      <div className="file-meta-list__cards">
        {files.map((staged) => {
          const preview = previews[staged.id];
          const hasErrors = (staged.validationErrors?.length ?? 0) > 0;
          const isLocked = disabled || staged.status === 'success';

          return (
            <div
              key={staged.id}
              className={[
                'file-meta-card',
                staged.status === 'uploading' && 'file-meta-card--uploading',
                staged.status === 'error' && 'file-meta-card--error',
                staged.status === 'success' && 'file-meta-card--success',
              ].filter(Boolean).join(' ')}
            >
              {staged.status === 'success' && (
                <div className="file-meta-card__overlay">Uploaded</div>
              )}

              <div className="file-meta-card__top">
                <div className="file-meta-card__thumb">
                  {preview ? (
                    <img src={preview} alt={staged.file.name} />
                  ) : (
                    <FontAwesomeIcon icon={mimeIcon(staged.file.type)} className="file-meta-card__icon" />
                  )}
                </div>
                <div className="file-meta-card__info">
                  <span className="file-meta-card__name" title={staged.file.name}>
                    {staged.file.name}
                  </span>
                  <span className="file-meta-card__size">{formatSize(staged.file.size)}</span>
                </div>
                {staged.status !== 'success' && (
                  <button
                    type="button"
                    className="file-meta-card__remove"
                    onClick={() => onRemove(staged.id)}
                    disabled={isLocked}
                    title="Remove file"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>

              <div className="file-meta-card__fields">
                {fields.filter((f) => !f.collapsed).map((field) =>
                  renderField(field, staged, hasErrors, isLocked, showCopyToAll, subcategories, onUpdateMetadata, applyToAll),
                )}

                {fields.some((f) => f.collapsed) && (
                  <details className="file-meta-card__more">
                    <summary>More details</summary>
                    <div className="file-meta-card__more-fields">
                      {fields.filter((f) => f.collapsed).map((field) =>
                        renderField(field, staged, hasErrors, isLocked, showCopyToAll, subcategories, onUpdateMetadata, applyToAll),
                      )}
                    </div>
                  </details>
                )}
              </div>

              {staged.validationErrors && staged.validationErrors.length > 0 && (
                <div className="file-meta-card__validation">
                  {staged.validationErrors.map((err, i) => (
                    <span key={i}>{err}</span>
                  ))}
                </div>
              )}
              {staged.error && (
                <div className="file-meta-card__error">{staged.error}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FileMetadataTable;
